import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';
import { StoreViewComponentDef } from '@df/catalog-ui/store/view/store-view.component.def';
import { IStoreDataV3, Store } from '@df/catalog/store/store';
import { StickyHideDirective } from '@df/legacy/ui/sticky/sticky-hide.directive';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { OptionImagePipe } from '@df/ui/common/option-image.pipe';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import sortBy from 'lodash-es/sortBy';
import { PageHeaderService } from '../../../../layout/page/header/page-header.service';
import { GoogleMapComponent } from '../../../../module/google-maps/google-map.component';
import { ICmsOutletBlockComponent } from '../../../outlet-block/icms-outlet-block.component';
import { StoreLinkComponent } from '../link/store-link.component';

declare module '@df/catalog/store/store' {
    interface IStoreDataV3 {
        images?: string[];
        nearest_stores: any;
        store_features: any;
    }
}

export interface StoreDay {
    status?: number;
    date?: Date;
    open?: string;
    close?: string;
}

@Component({
    selector: 'store-view',
    templateUrl: 'store-view.component.html',
    styleUrl: './store-view.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        GoogleMapComponent,
        GridModule,
        ICmsOutletBlockComponent,
        OptionImagePipe,
        OptionLabelPipe,
        RouterModule,
        TranslatePipe,
        SizeClassDirective,
        StickyHideDirective,
        StoreLinkComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreViewComponent extends StoreViewComponentDef<Store<IStoreDataV3>> implements OnDestroy {
    protected pageHeaderService = inject(PageHeaderService);

    /** Last hash */
    @SessionStorage('lastStoreFinderHash')
    private lastHash?: string;

    /**
     * Map options
     */
    readonly mapOptions: google.maps.MapOptions = {
        zoom: 14,
        streetViewControl: true
    };

    weeklySchedule: StoreDay[] = [];

    get backUrl() {
        return '/about-us/store-locator' + (this.lastHash || '');
    }

    override ngOnDestroy() {
        super.ngOnDestroy();

        // reset page header settings
        this.timeoutService.platformSafe(() => this.pageHeaderService.resetHighPriorityState()); // injector may be destroyed
    }

    override onModelChange(): void {
        this.weeklySchedule = this.getWeeklySchedule();

        super.onModelChange();

        this.updateClassNames();
    }

    protected getWeeklySchedule(): StoreDay[] {
        /**
         * all available days for store
         */
        const openingHours = this.model?.openingHours;
        let weeklySchedule: StoreDay[] = [];

        if (openingHours?.length) {
            /**
             * sorted last 7 days
             */
            weeklySchedule = sortBy(openingHours.slice(0, 7), (day: any) => day.date && day.date.getDay()) as StoreDay[];

            /**
             * shifting sunday to the end of the week
             */
            weeklySchedule.push(<StoreDay>weeklySchedule.shift());
        }

        return weeklySchedule;
    }

    protected updateClassNames(): void {
        this.pageHeaderService.setHighPriorityState({
            transparent: false,
            lightMode: false,
            hideUp: false
        });
    }
}
